<template>
  <div class="device-background">
    <div id="deviceSelector">
      <div class="selector-container">
        <span class="title" aria-live="assertive">{{ $gettext('Configuración de audio') }}</span>

        <div v-if="isLoadingDevices" class="loading-container">
          <div class="spinner"></div>
          <p>{{ $gettext('Cargando dispositivos...') }}</p>
        </div>

        <div v-else>
          <div class="select">
            <label for="audioSource">{{ $gettext('Micrófono:') }}</label>
            <select
                ref="audioInputSelect"
                @change="setAudioIn"
                name="audioInput"
                id="audioSource"></select>
          </div>
          <div class="select">
            <label for="audioOutput">{{ $gettext('Altavoces:') }}</label>
            <select
                ref="audioOutputSelect"
                @change="setAudioOut"
                name="audioOutput"
                id="audioOutput">
                <div class="speakers"></div>
            </select>
          </div>
        </div>
      </div>
      <div class="button-container">
        <button
            class="cancel"
            @click="closeModal"
            name="cancelDeviceSelection">{{ $gettext('Cancelar') }}
        </button>
        <button
            class="save-devices"
            :disabled="!hasDevices || isLoadingDevices"
            :title="!hasDevices ? $gettext('Necesitas micrófono y altavoces para hacer la llamada') : ''"
            :class="{ 'disabled': !hasDevices || isLoadingDevices }"
            name="startCall"
            @click="startVideoCall" >{{ $gettext('Llamar') }}</button>
      </div>
    </div>
  </div>

</template>

<script>

import store from "@/store";
import {mapActions, mapMutations} from "vuex";
import {EventBus} from "@/event-bus";

import { DeviceSelection } from "@/../../videochat/js/VideoCall";

export default {
  name: "DeviceSelector",
  data(){
    return{
      deviceSelector: null,
      audioInputSelect: null,
      audioOutputSelect: null,
      hasDevices: false,
      selectors: [],
      isLoadingDevices: false,
    }
  },
  mounted() {
    this.initializeSelector();
  },
  methods: {
    ...mapMutations(['updateCall', "showCallForm", 'setAudioOutput', 'setAudioInput']),
    ...mapActions(['sendCall']),

    initializeSelector(){

      this.deviceSelector = new DeviceSelection({
          selectVideo: false,
          selectAudioInput: true,
          selectAudioOutput: true
      });

      this.isLoadingDevices = true;

      this.deviceSelector.getDeviceOptions().then((devices)=>{
        this.isLoadingDevices = false;
        // Wait for the next tick to ensure refs are available after v-else is rendered
        this.$nextTick(() => {
          this.audioInputSelect = this.$refs.audioInputSelect;
          this.audioOutputSelect = this.$refs.audioOutputSelect;
          this.gotDevices(devices);
        });
      }).catch((error) => {
        console.error('Error loading devices:', error);
        this.isLoadingDevices = false;
      })

      this.deviceSelector.onDevicesUpdated = (devices) => {
        this.$nextTick(() => {
          this.gotDevices(devices);
        });
      };

    },

    // Los dispositivos encontrados los mostrará en un select
    gotDevices(deviceInfos) {
      this.clearDeviceSelectors();
      const values = this.selectors.map(select => select.value);
      this.selectors.forEach(select => {
        while (select.firstChild) {
          select.removeChild(select.firstChild);
        }
      });
      
      let hasAudioInput = false;
      let hasAudioOutput = false;
      
      for(let i = 0; i !== deviceInfos.length; ++i){
        const deviceInfo = deviceInfos[i];
        const option = document.createElement('option');
        option.value = deviceInfo.deviceId;
        // Separa por tipo audio Input
        if (deviceInfo.kind === 'audioinput'){
          option.text = deviceInfo.label || `microphone ${this.audioInputSelect.length + 1}`;
          this.audioInputSelect.appendChild(option);
          hasAudioInput = true;

          // Separa por tipo audio Output
        } else if (deviceInfo.kind === 'audiooutput') {
          option.text = deviceInfo.label || `speaker ${this.audioOutputSelect.length + 1}`;
          this.audioOutputSelect.appendChild(option);
          hasAudioOutput = true;
        }
      }

      // Enable button only if we have both audio input and output devices
      this.hasDevices = hasAudioInput;

      this.selectors.forEach((select, selectorIndex) => {
        if (Array.prototype.slice.call(select.childNodes).some(n => n.value === values[selectorIndex])) {
          select.value = values[selectorIndex];
        }
      })
    },

    clearDeviceSelectors(){
    
      while (this.audioInputSelect.firstChild) {
        this.audioInputSelect.removeChild(this.audioInputSelect.firstChild);
      }

      while (this.audioOutputSelect.firstChild) {
        this.audioOutputSelect.removeChild(this.audioOutputSelect.firstChild);
      }

    },


    // Cierra el modal de los devices
    closeModal() {
      // Stop all preview tracks when closing modal
      if (this.deviceSelector) {
        this.deviceSelector.stopAllTracks();
      }
      this.$emit('closeModal');
    },

    // Inicia la conexión con el start call
    startVideoCall() {
      // Stop all preview tracks before starting the call
      if (this.deviceSelector) {
        this.deviceSelector.stopAllTracks();
      }

      EventBus.$emit("startVideoCall");
      this.showCallForm(false);
      this.$emit('closeModal');

    },

    // Se almacena el audio input seleccionado en una variable
    setAudioIn(event) {
      const selectedDeviceId = event.target.value;
      this.deviceSelector.setSelectedAudioDevice(selectedDeviceId);
      this.setAudioInput(selectedDeviceId); // Actualiza el estado en Vuex o realiza la acci?n que desees
    },

    // Se almacena el audio output seleccionado en una variable
    setAudioOut(event) {
      const selectedDeviceId = event.target.value;
      this.deviceSelector.setSelectedAudioOutputDevice(selectedDeviceId);
      this.setAudioOutput(selectedDeviceId); // Actualiza el estado en Vuex o realiza la acci?n que desees
    },

    handleError(error) {
      console.log('navigator.MediaDevices.getUserMedia error: ', error);
    }
  }
}
</script>

<style scoped>
.selector-container {
  min-height: 150px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  min-height: 150px;
}

.loading-container p {
  margin-top: 1rem;
  color: #666;
  font-size: 14px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>